import React from 'react';
import {
  colors,
  createStyles,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  withStyles,
} from '@material-ui/core';
import { change, Field } from 'redux-form';
import { hasAtLeastOneChecked, required } from 'app/util/validators';
import { renderCheckboxBase } from 'app/components/FormFields/_renderCheckboxBase';
import { FieldNames } from 'app/models/fields/names';
import { HOME_EQUITY_DEBT_CONSOLIDATION_OPTIONS } from 'app/models/options/options';
import { HomeEquityLoanPurpose } from '@lenderful/domain';

const styles = (theme) => createStyles({
  selected: {
    [theme.breakpoints.down('sm')]: {
      background: theme.palette.secondary.main,
      '& span': {
        color: '#ffffff',
      },
    },
  },
  radioLabel: {
    marginTop   : 5,
    marginRight : 0,
    fontWeight  : 400,
    [theme.breakpoints.down('sm')]: {
      marginTop    : 0,
      paddingTop   : 2,
      paddingBottom: 2,
      borderBottom : `1px solid ${colors.grey[300]}`,
    },
  },
  radioWrapper: {
    marginTop: 15,
    '& label': {
      marginRight: 0,
      fontWeight : 400,
      [theme.breakpoints.down('sm')]: {
        flexDirection : 'row-reverse',
        justifyContent: 'space-between',
        marginLeft    : 0,
        '& span:last-child': {
          marginLeft: 10,
        },
      },
    },
  },
  subOptionsTitle: {
    fontWeight  : 600,
    marginBottom: 4,
    fontSize    : '0.875rem',
    color       : colors.grey[700],
  },
  subOptionsPanel: {
    borderLeft : `3px solid ${colors.grey[300]}`,
    paddingLeft: 16,
    marginTop  : 15,
    [theme.breakpoints.down('sm')]: {
      borderLeft : 'none',
      borderTop  : `3px solid ${colors.grey[300]}`,
      paddingLeft: 0,
      paddingTop : 12,
      marginTop  : 8,
    },
  },
});

const renderRadioGroup = (props) => {
  const { classes, input, meta } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    input.onChange(event);
    if (event.target.value !== HomeEquityLoanPurpose.CONSOLIDATION) {
      meta.dispatch(change(meta.form, FieldNames.homeEquityDebtConsolidationType, []));
    }
  };

  return (
    <FormControl fullWidth>
      <RadioGroup {...input} onChange={handleChange}>
        {props.options.map((option, index) => (
          <FormControlLabel
            key       = {index}
            control   = {<Radio color='primary' />}
            label     = {option.title}
            value     = {option.value}
            className = {input.value === option.value ? `${classes.radioLabel} ${classes.selected}` : classes.radioLabel}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

export const HomeEquityLoanPurposeRadio = withStyles(styles)(({ classes, ...props }: any) => {
  const isDebtConsolidation    = props.values?.[FieldNames.homeEquityLoanPurpose] === HomeEquityLoanPurpose.CONSOLIDATION;
  const showDebtTypeSection    = isDebtConsolidation && props.excludedOptions?.includes('PAYOFF');
  const debtConsolidationOptions = props.excludedOptions?.includes('PAYOFF')
    ? HOME_EQUITY_DEBT_CONSOLIDATION_OPTIONS
    : [];

  return (
    <Grid container spacing={2} className={classes.radioWrapper}>
      <Grid item xs={12} sm={showDebtTypeSection ? 6 : 12}>
        <Field
          classes   = {classes}
          component = {renderRadioGroup}
          name      = {props.name}
          onChange  = {props.onChange}
          options   = {props.options}
          validate  = {[required]}
        />
      </Grid>
      {showDebtTypeSection && (
        <Grid item xs={12} sm={6}>
          <div className={classes.subOptionsPanel}>
            <Typography className={classes.subOptionsTitle}>What are you consolidating?</Typography>
            <FormGroup>
              <Field
                component = {renderCheckboxBase}
                name      = {FieldNames.homeEquityDebtConsolidationType}
                options   = {debtConsolidationOptions}
                validate  = {[hasAtLeastOneChecked]}
              />
            </FormGroup>
          </div>
        </Grid>
      )}
    </Grid>
  );
});
